<template>
  <div>
    <div v-if="loading" class="flex h-full justify-center items-center">
      <loading-spinner diameter="64px"/>
    </div>

    <alert-badge
        v-else-if="error.title"
        isDanger
        :title="error.title"
        :body="error.body"
        @dismissed="
        () => {
          error.title = ''
          error.body = ''
        }
      "
    />

    <alert-badge
        v-else-if="successMsg"
        isSuccess
        :title="successMsg"
        @dismissed="
        () => {
          this.successMsg = ''
        }
      "
    />

    <div dir="rtl" class="flex justify-center flex-col items-center">
      <div class="m-12">
        <clock/>
      </div>

      <div v-if="lastStatus.id"
           class="flex flex-col items-center justify-center my-8">
        <div>{{ t('lastStatusType') }}: {{ t(lastStatus.clockType) }}</div>
        <div>{{ t('lastStatusTime') }}: {{
            parseFormat(lastStatus.updatedAt)
          }}
        </div>
      </div>

      <div class="flex gap-5 mx-2">
        <el-button style="width: 100px; height: 50px;" type="primary"
                   @click="openCashierPinDialog(clockIn)"> {{ t('clockIn') }}
        </el-button>
        <el-button style="width: 100px; height: 50px;" type="danger"
                   @click="openCashierPinDialog(clockOut)">{{ t('clockOut') }}
        </el-button>
      </div>
    </div>
  </div>
  <el-dialog v-model="cashierPinDialogOpen" width="30%">
    <div>
      <!-- Enter the pin -->
      <div class="my-2">
        <p>{{ t('dialogs.enterCashierPin') }}</p>
      </div>

      <!-- password input up to 6 chars -->
      <div class="my-2">
        <el-input
            id="password"
            v-model="cashierPin"
            type="password"
            required
            show-password
            autofocus
            dir="ltr"
            :maxlength="4"
            :minlength="4"
            ref="cashierPinInput"
            @keyup.enter="cashierPinAction()"
        />
      </div>

      <!-- finish payment -->
      <div class="flex items-center justify-between mb-2">
        <horizontal-button
            class="text-lg complete-payment"
            :title="t('dialogs.confirm')"
            @click="cashierPinAction()"
            :rounded="false"
        />
      </div>
    </div>
  </el-dialog>
</template>

<script lang="ts">
import {defineComponent, nextTick, onMounted, reactive, ref} from 'vue'

import AlertBadge from '@/components/AlertBadge.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import Clock from '@/components/Clock.vue'

import {useHTTP} from '@/plugins/http'
import {useContext} from '@/plugins/context'
import {useI18n} from 'vue-i18n'
import {parseFormat} from '@/utils/date'

import EmployeeClock from '@/models/hr/EmployeeClock'
import HorizontalButton from '@/components/HorizontalButton.vue'
import {useSettingsStore} from "@/stores/settingsStore";

export default defineComponent({
  setup() {
    const loading = ref(false)
    const error = reactive({title: '', body: ''})

    const {get, post} = useHTTP()
    const ctx = useContext()

    const cashierPinDialogOpen = ref(false)
    const cashierPin = ref('')
    const cashierPinInput = ref()
    const cashierPinAction: any = ref();

    const {t} = useI18n({
      messages: {
        en: {
          clockIn: 'Clock in',
          clockOut: 'Clock out',
          clockInSuccess: 'Clocked in successfully',
          clockOutSuccess: 'Clocked out successfully',
          in: 'Clocked in',
          out: 'Clocked out',
          lastStatusType: 'Last log type',
          lastStatusTime: 'Last log time'
        },

        ar: {
          clockIn: 'تسجيل الدخول',
          clockOut: 'تسجيل الخروج',
          clockInSuccess: 'تم تسجيل الدخول بنجاح',
          clockOutSuccess: 'تم تسجيل الخروج بنجاح',
          in: 'دخول',
          out: 'خروج',
          lastStatusType: 'نوع اخر تسجيل',
          lastStatusTime: 'تاريخ اخر تسجيل'
        }
      }
    })

    const successMsg = ref('')

    const resetError = () => {
      error.title = ''
      error.body = ''
    }

    const openCashierPinDialog = (action: any) => {
      cashierPinAction.value = action;
      if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
        cashierPinDialogOpen.value = true
        nextTick(() => {
          setTimeout(() => {
            cashierPinInput.value.focus()
          }, 0)
        })
        return;
      }
      action();
    }

    const resetSuccessMsg = () => {
      successMsg.value = ''
    }

    const clockIn = async () => {
      error.title = '';
      if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
        if (cashierPin.value === '') {
          cashierPinDialogOpen.value = false
          error.title = t('dialogs.errCashierPinRequired')
          return
        }
      }


      loading.value = true
      resetError()
      resetSuccessMsg()

      try {
        const data = await post<EmployeeClock>(
            EmployeeClock.ENDPOINT,
            EmployeeClock.from({
              clockType: 'in',
              userID: ctx.currentUser.id,
              locationID: ctx.currentLocation.id,
              registerID: ctx.currentRegister.id
            }),
            {headers: {pin: cashierPin.value}}
        )

        successMsg.value = `(${data.user?.name}) ${t('clockInSuccess')}`
        setTimeout(() => {
          successMsg.value = '';
        }, 3000);
      } catch (err) {
        error.title = err?.title
        error.body = err?.body
        setTimeout(() => {
          error.title = '';
          error.body = '';
        }, 3000);
      }
      cashierPinDialogOpen.value = false
      cashierPin.value = "";
      loading.value = false
    }

    const clockOut = async () => {
      error.title = '';

      if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
        if (cashierPin.value === '') {
          cashierPinDialogOpen.value = false
          error.title = t('dialogs.errCashierPinRequired')
          return
        }
      }

      loading.value = true
      resetError()
      resetSuccessMsg()

      try {
        const data = await post<EmployeeClock>(
            EmployeeClock.ENDPOINT,
            EmployeeClock.from({
              clockType: 'out',
              userID: ctx.currentUser.id,
              locationID: ctx.currentLocation.id,
              registerID: ctx.currentRegister.id
            }),
            {headers: {pin: cashierPin.value}}
        )

        successMsg.value = `(${data.user?.name}) ${t('clockOutSuccess')}`
        setTimeout(() => {
          successMsg.value = '';
        }, 3000);
      } catch (err) {
        error.title = err?.title
        error.body = err?.body
        setTimeout(() => {
          error.title = '';
          error.body = '';
        }, 3000);
      }
      cashierPinDialogOpen.value = false
      cashierPin.value = "";
      loading.value = false
    }

    const lastStatus = ref(new EmployeeClock())
    const fetchLastEmployeeClockStatus = async () => {
      loading.value = true
      resetError()
      resetSuccessMsg()
      const userID = ctx.currentUser.id
      if (!userID) {
        return
      }

      try {
        const resp = await get<EmployeeClock[]>(
            `${EmployeeClock.ENDPOINT}?user_id=${userID}&limit=1`
        )
        if (resp.length) lastStatus.value = EmployeeClock.from(resp[0])
      } catch (err) {
        if (err.code != 404) {
          error.title = err?.title
          error.body = err?.body
          setTimeout(() => {
            error.title = '';
            error.body = '';
          }, 3000);
        }
      }

      loading.value = false
    }

    onMounted(() => {
      fetchLastEmployeeClockStatus()
    })

    return {
      loading,
      error,
      clockIn,
      clockOut,
      t,
      successMsg,
      lastStatus,
      cashierPinDialogOpen,
      cashierPin,
      cashierPinInput,
      openCashierPinDialog,
      cashierPinAction
    }
  },

  components: {
    AlertBadge,
    LoadingSpinner,
    Clock,
    HorizontalButton
  },

  methods: {
    parseFormat
  }
})
</script>
